import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { AdminReport } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";

/** Mirrors `apps/admin-web/src/pages/ReportsPage.tsx`'s `reportMeta` verbatim. */
function reportMeta(r: AdminReport): string {
  const parts = [`${r.targetType}:${r.targetId}`, r.reason, r.status];
  if (r.note) parts.push(`"${r.note}"`);
  return parts.join(" · ");
}

/**
 * Moderation queue, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v2) — mirrors
 * `apps/admin-web/src/pages/ReportsPage.tsx` (itself mirroring `apps/admin/app/(tabs)/reports.tsx` /
 * `specs/admin/screens/moderation-queue.md`): list filed reports and dismiss/take-down the open ones.
 * Every `useQuery`/`useMutation` here is lifted UNCHANGED — only the returned JSX differs.
 */
export function ReportsPage() {
  const { client, token } = useAuth();
  const queryClient = useQueryClient();
  const [busyReportIds, setBusyReportIds] = useState<Set<string>>(() => new Set());

  const reportsQuery = useQuery({
    queryKey: ["admin-reports"],
    queryFn: () => client!.adminReports(token!),
    enabled: !!client && !!token,
  });
  const reports = reportsQuery.data ?? [];

  const actMutation = useMutation({
    mutationFn: ({ report, decision }: { report: AdminReport; decision: "dismiss" | "takedown" }) =>
      client!.adminReportAction(token!, { reportId: report.id, decision }),
    onMutate: ({ report }) => setBusyReportIds((ids) => new Set(ids).add(report.id)),
    onSuccess: (updated) =>
      queryClient.setQueryData<AdminReport[]>(["admin-reports"], (list) =>
        (list ?? []).map((r) => (r.id === updated.id ? updated : r)),
      ),
    onSettled: (_data, _error, { report }) =>
      setBusyReportIds((ids) => {
        const next = new Set(ids);
        next.delete(report.id);
        return next;
      }),
  });

  return (
    <MainCard title="Moderation queue">
      {reportsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(reportsQuery.error as Error).message}
        </Alert>
      ) : null}
      {actMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(actMutation.error as Error).message}
        </Alert>
      ) : null}
      {reportsQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 0 }}>
          No reports filed yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Report</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const pending = r.status === "open" || r.status === "reviewing";
                // docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3 — `actMutation` is ONE shared
                // mutation for every row, so `actMutation.isPending` used to disable EVERY
                // row's buttons while any one report was being actioned. Scope the busy
                // state to THIS report's own row. A Set is intentional: separate reports
                // can mutate concurrently, while each pending row remains disabled until
                // its own request settles.
                const rowPending = busyReportIds.has(r.id);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>{reportMeta(r)}</TableCell>
                    <TableCell>
                      <Chip label={r.status} color={pending ? "warning" : "default"} size="small" />
                    </TableCell>
                    <TableCell>
                      {pending ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled={rowPending}
                            onClick={() => actMutation.mutate({ report: r, decision: "dismiss" })}
                          >
                            Dismiss
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={rowPending}
                            onClick={() => actMutation.mutate({ report: r, decision: "takedown" })}
                          >
                            Take down
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Already handled
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </MainCard>
  );
}
